// Simple's routes, one tree (specs ui-perf/simple-home and ui-perf/simple-flows): Home, the guided
// page and Home's two composers. They are composed here because the guided page's ad step joins
// two features (spec
// ui-perf/simple-studio, PR s3): Simple's page (`features/studio2/simple`) with the templates
// area's own form panel (`features/templates`), unchanged. A feature never imports another; the
// composition root may.
import { Skeleton } from "@mantine/core";
import { createRoute, redirect, useLocation, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { z } from "zod";

import { useListTemplates } from "../api/generated/endpoints/generation/generation";
import type { Job, TemplateCard } from "../api/generated/model";
import { studioFrameRoute } from "../features/studio2/routes";
import { GUIDED_KINDS, guidedPath } from "../features/studio2/simple/guided";
import { SimpleAds } from "../features/studio2/simple/SimpleAds";
import {
  SimpleActorPage,
  SimpleImageAdPage,
  SimpleSoundPage,
  SimpleToolPage,
  SimpleUgcPage,
} from "../features/studio2/simple/SimpleFlows";
import { SimpleHome } from "../features/studio2/simple/SimpleHome";
import { GALLERY_KINDS } from "../features/studio2/generations/GenerationsGallery";
import { SimpleFix } from "../features/studio2/simple/SimpleFix";
import { SimpleImages } from "../features/studio2/simple/SimpleImages";
import { SimpleLibrary } from "../features/studio2/simple/SimpleLibrary";
import { SimpleVideos } from "../features/studio2/simple/SimpleVideos";
import { isSoundSlug } from "../features/studio2/simple/sounds";
import { isToolSlug } from "../features/studio2/simple/video-tools";
import { SimplePage } from "../features/studio2/simple/SimplePage";
import { TEMPLATE_READ, TemplateCards, TemplateFormCard } from "../features/studio2/simple/TemplateCards";
import { FORMS } from "../features/templates/forms";
import { TemplatePanel } from "../features/templates/TemplatePanel";
import { InlinePanels } from "../ui/GlassPanel";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/studio/simple/make";

// `?kind=`: what the guided page makes; anything else is none chosen yet. `?template=`: an ad's
// template, by the Templates page's own rule for a slug (spec ui-perf/simple-home-rows); anything
// else is dropped.
const simpleSearch = z.object({
  kind: z.enum(GUIDED_KINDS).optional().catch(undefined),
  template: z
    .string()
    .max(64)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .optional()
    .catch(undefined),
});

/** Simple, Pro's sibling under the studio frame (spec ui-perf/ui-paths-simple-pro). */
export const simpleRoute = createRoute({
  getParentRoute: () => studioFrameRoute,
  path: "simple",
});

/**
 * Simple's Home. A link to the guided page from before Home (`simple?kind=` with one of its kinds)
 * opens it at `make`, its whole query and hash kept and the history entry replaced; the old
 * `home2/simple?kind=` goes there in the shared redirect (`features/studio2/routes.ts`).
 */
export const simpleHomeRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "/",
  beforeLoad: ({ location }) => {
    const to = guidedPath(location.pathname, location.search as { kind?: unknown });
    if (to === location.pathname) return;
    const hash = location.hash === "" ? "" : `#${location.hash}`;
    throw redirect({ href: `${to}${location.searchStr}${hash}`, replace: true });
  },
  // Its Templates row offers what the guided page can run, whose forms are composed here.
  component: () => <SimpleHome usable={usable} />,
});

/** The guided page (spec ui-perf/simple-studio), unchanged: Create and the cards open it. */
export const simpleMakeRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "make",
  validateSearch: (search) => simpleSearch.parse(search),
  component: ProSimple,
});

/** Home's UGC video ad: the Talking Head composer in Simple's frame. */
export const simpleUgcRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "ugc-video-ad",
  component: SimpleUgcPage,
});

/** Simple's Ads section, its Create tab (spec ui-perf/simple-ads). */
export const simpleAdsRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "ads",
  component: SimpleAds,
});

/** Simple's Videos section (spec ui-perf/simple-videos). */
export const simpleVideosRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "videos",
  component: SimpleVideos,
});

/** A video tool's page (spec ui-perf/simple-video-tools); a slug of no tool is not found. */
export const simpleToolRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "videos/$tool",
  params: { parse: ({ tool }) => (isToolSlug(tool) ? { tool } : false) },
  component: SimpleToolPage,
});

/** A sound's page, from Create's picker (spec ui-perf/simple-sounds); a slug of no sound is not found. */
export const simpleSoundRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "audio/$sound",
  params: { parse: ({ sound }) => (isSoundSlug(sound) ? { sound } : false) },
  component: SimpleSoundPage,
});

/** Home's Image ad: Ad static in Simple's frame. */
export const simpleImageAdRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "image-ad",
  component: SimpleImageAdPage,
});

/** Simple's Images section (spec ui-perf/simple-images). */
export const simpleImagesRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "images",
  component: SimpleImages,
});

/**
 * Simple's Library (spec ui-perf/simple-library): `?kind=`, a kind of the gallery's (its own, not
 * the guided page's); `?job=`, a job whose detail opens over it. Anything else is dropped.
 */
const librarySearch = z.object({
  kind: z.enum(GALLERY_KINDS).optional().catch(undefined),
  job: z.string().uuid().optional().catch(undefined),
});

export const simpleLibraryRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "library",
  validateSearch: (search) => librarySearch.parse(search),
  component: SimpleLibrary,
});

/** Images' Fix this image, the prototype's wizard (spec ui-perf/simple-fix-image). */
export const simpleFixRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "fix-image",
  component: SimpleFix,
});

/** Images' Make an actor: the AI Actors page's composer in Simple's frame. */
export const simpleActorRoute = createRoute({
  getParentRoute: () => simpleRoute,
  path: "actor",
  component: SimpleActorPage,
});

function ProSimple() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  // Create's fresh start (spec ui-perf/simple-shell, decision 4).
  const start = useLocation({ select: (location) => location.state.simpleStart });
  // Another brand, or Create, starts the page anew: no draft, template or result of before.
  return (
    <SimplePage
      key={`${teamId} ${brandId} ${start ?? 0}`}
      template={(onStarted) => <SimpleTemplate teamId={teamId} brandId={brandId} onStarted={onStarted} />}
    />
  );
}

/** A template with a form built here: the rest open nothing, as their cards on the Templates page. */
const usable = (template: TemplateCard) => FORMS[template.id] !== undefined;

/**
 * The templates, then a chosen one's own form in the page's flow; its runs go to Simple's result.
 * The chosen one is the link's (`?template=`), so Home's cards open it and a reload keeps it; one
 * that cannot be run here leaves the list (spec ui-perf/simple-home-rows).
 */
function SimpleTemplate(props: { teamId: string; brandId: string; onStarted: (job: Job) => void }) {
  const { teamId, brandId, onStarted } = props;
  const named = useSearch({ from: ROUTE, select: (search) => search.template });
  const navigate = useNavigate({ from: "/teams/$teamId/brands/$brandId/simple/make" });
  // The step's own read (`TemplateCards`'), so naming one costs no request of its own.
  const templates = useListTemplates(teamId, TEMPLATE_READ, { query: { enabled: named !== undefined } });
  const choose = (template: TemplateCard | null) =>
    void navigate({ search: (previous) => ({ ...previous, template: template?.id }) });
  const chosen = templates.data?.items.find(
    (each) => each.id === named && each.available && !each.coming_soon && usable(each),
  );
  if (named !== undefined && templates.data === undefined && !templates.isError) return <Skeleton height={240} />;
  if (chosen === undefined) return <TemplateCards teamId={teamId} usable={usable} onPick={choose} />;
  return (
    <TemplateFormCard onBack={() => choose(null)}>
      <InlinePanels value>
        <TemplatePanel
          key={chosen.id}
          teamId={teamId}
          brandId={brandId}
          template={chosen}
          replicate={false}
          onClose={() => choose(null)}
          onStarted={onStarted}
        />
      </InlinePanels>
    </TemplateFormCard>
  );
}
